/**
 * The decision log of the role's search (Theresa, Oct 22 2026): every step of the search learning —
 * what the agent proposed and from which signals, which proposals were replaced, each bias check,
 * each recruiter's accept / decline / undo, hand edits and rebuilds — with who and when. Newest
 * first, folded by default.
 */
import type { SearchLogEntry } from '../../types/taOutreach';
import { ruleText } from './searchRuleText';

const ACTION: Record<SearchLogEntry['action'], string> = {
  built: 'Search built',
  edited: 'Search edited',
  proposed: 'Suggested',
  no_proposal: 'No suggestion',
  replaced: 'Suggestion replaced by newer ones',
  challenged: 'Bias check',
  accepted: 'Accepted',
  declined: 'Declined',
  withdrawn: 'Undone',
};

const RISK = { low: 'low bias risk', possible: 'possible bias risk', high: 'high bias risk' };

/** "Sofia" from sofia.struempfler@finn.com; "Agent" for the agent. */
const who = (by: string) => (by === 'agent' ? 'Agent' : (by.split('@')[0].split('.')[0] || by).replace(/^./, (c) => c.toUpperCase()));

const when = (at: string) => {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export function SearchLog({ entries }: { entries: SearchLogEntry[] }) {
  if (!entries.length) return null;
  const newest = [...entries].reverse();
  return (
    <details className="text-xs">
      <summary className="cursor-pointer font-semibold text-[#707070]">Decision log ({entries.length})</summary>
      <ol className="m-0 mt-1 list-none space-y-1.5 p-0" aria-label="Decision log">
        {newest.map((e, i) => (
          <li key={`${e.at}-${i}`} className="border-l-2 border-solid border-y-0 border-r-0 border-[#E9EAEC] pl-2">
            <span className="text-[#979797]">{when(e.at)}</span> · <b>{who(e.by)}</b> · {ACTION[e.action] || e.action}
            {e.rule && <span>: {ruleText(e.rule)}</span>}
            {e.risk && <span className={e.risk === 'high' ? ' text-[#E71D3F]' : e.risk === 'possible' ? ' text-[#B96716]' : ' text-[#1D9247]'}> ({RISK[e.risk]})</span>}
            {e.why && <span className="block text-[#707070]">{e.action === 'challenged' ? 'Check: ' : e.action === 'declined' || e.action === 'withdrawn' ? 'Reason: ' : 'Why: '}{e.why}</span>}
            {e.note && <span className="block text-[#707070]">{e.note}</span>}
          </li>
        ))}
      </ol>
    </details>
  );
}
